import axios from 'axios';
import { LoginRequest, LoginResponse } from '../types/auth';
import { loginPathFor } from '../utils/returnTo';

// Dev: REACT_APP_API_URL=http://localhost:3000 (set in frontend/.env)
// Production single-deployment: not set in Dockerfile → CRA inlines as 'undefined' string or ''
// Either way — falsy/undefined → empty string → axios uses relative paths (same origin)
const _apiUrl = process.env.REACT_APP_API_URL;
const API_BASE_URL = (_apiUrl && _apiUrl !== 'undefined') ? _apiUrl : '';

class ApiService {
  public api: any;
  private jiraConfigCache: any = null;
  private jiraConfigPromise: Promise<any> | null = null;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include auth token
    this.api.interceptors.request.use(
      (config: any) => {
        const token = localStorage.getItem('auth_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error: any) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor to handle auth errors. A 401 from an auth endpoint
    // (login/google/register) is a failed sign-in attempt — let the caller show the
    // error instead of redirecting. Only redirect on 401s from authenticated requests
    // (i.e. an expired/invalid session token).
    this.api.interceptors.response.use(
      (response: any) => response,
      (error: any) => {
        const url: string = error.config?.url || '';
        const isAuthAttempt = /\/auth\/(login|google|register)/.test(url);
        if (error.response?.status === 401 && !isAuthAttempt) {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user');
          // Sign in again and come back to the page that was open when the session ended.
          window.location.href = loginPathFor(window.location.pathname + window.location.search + window.location.hash);
        }
        return Promise.reject(error);
      }
    );
  }

  // Authentication APIs
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await this.api.post('/auth/login', credentials);
    return response.data;
  }

  // Exchange a Google ID token for our app JWT.
  async googleLogin(credential: string): Promise<LoginResponse> {
    const response = await this.api.post('/auth/google', { credential });
    return response.data;
  }

  async getCurrentUser() {
    const response = await this.api.get('/auth/me');
    return response.data;
  }

  // Worklog APIs
  async getWorklogsWithParams(params: {
    siteBaseUrl: string;
    accessToken: string;
    email?: string;
    from?: string;
    to?: string;
    tz?: string;
  }) {
    const response = await this.api.get('/worklog', { params });
    return response.data;
  }

  async getTodayWorklogs(params: {
    siteBaseUrl: string;
    accessToken: string;
    email?: string;
    tz?: string;
  }) {
    const response = await this.api.get('/worklog/today', { params });
    return response.data;
  }

  async getThisWeekWorklogs(params: {
    siteBaseUrl: string;
    accessToken: string;
    email?: string;
    tz?: string;
  }) {
    const response = await this.api.get('/worklog/this-week', { params });
    return response.data;
  }

  async getThisMonthWorklogs(params: {
    siteBaseUrl: string;
    accessToken: string;
    email?: string;
    tz?: string;
  }) {
    const response = await this.api.get('/worklog/this-month', { params });
    return response.data;
  }

  // Database APIs
  async getAllWorklogs(days?: number) {
    const response = await this.api.get('/db/all-worklogs', {
      params: days ? { days } : {}
    });
    return response.data;
  }

  async getDatabaseStatus() {
    const response = await this.api.get('/db/status');
    return response.data;
  }

  // User management APIs
  async getUsers(params: {
    siteBaseUrl: string;
    accessToken: string;
    email?: string;
  }) {
    const response = await this.api.get('/users', { params });
    return response.data;
  }

  // Issue Management APIs
  async getIssuesCount() {
    const response = await this.api.get('/db/issues/count');
    return response.data;
  }

  async syncIssuesWithParams(params: {
    siteBaseUrl: string;
    accessToken: string;
    email?: string;
    maxIssues?: number;
    incremental?: boolean;
  }) {
    const response = await this.api.get('/sync-issues', { params });
    return response.data;
  }

  // Health check
  async healthCheck() {
    const response = await this.api.get('/health');
    return response.data;
  }

  // Get JIRA config from database (admin-configured) or fallback to env vars
  private async getJiraConfig() {
    // Return cached config if available
    if (this.jiraConfigCache) return this.jiraConfigCache;
    if (this.jiraConfigPromise) return this.jiraConfigPromise;

    // Create new promise
    this.jiraConfigPromise = this.fetchJiraConfig();

    try {
      const config = await this.jiraConfigPromise;
      this.jiraConfigCache = config;
      return config;
    } finally {
      this.jiraConfigPromise = null;
    }
  }

  private async fetchJiraConfig() {
    try {
      // Try to get credentials from database (admin-configured)
      const response = await this.api.get('/jira-credentials');

      if (response.data.success && response.data.data) {
        if (response.data.data.siteBaseUrl && response.data.data.email) {
          return {
            siteBaseUrl: response.data.data.siteBaseUrl,
            accessToken: process.env.REACT_APP_JIRA_ACCESS_TOKEN || 'your_api_token',
            email: response.data.data.email
          };
        }
      }
    } catch (error) {
      console.warn('Failed to get JIRA credentials from database, falling back to environment variables:', error);
    }

    return {
      siteBaseUrl: process.env.REACT_APP_JIRA_SITE_URL || 'https://yourcompany.atlassian.net',
      accessToken: process.env.REACT_APP_JIRA_ACCESS_TOKEN || 'your_api_token',
      email: process.env.REACT_APP_JIRA_EMAIL || 'your_email@company.com'
    };
  }

  // Refresh APIs - Let backend handle JIRA credentials from database
  async refreshSyncIssues() {
    const response = await this.api.get('/sync-issues', { params: { source: 'refresh' } });
    return response.data;
  }


  // JIRA Credentials Management (Admin only)
  async getJiraCredentials() {
    const response = await this.api.get('/jira-credentials');
    return response.data;
  }

  async updateJiraCredentials(credentials: {
    siteBaseUrl: string;
    accessToken: string;
    email: string;
  }) {
    const response = await this.api.put('/jira-credentials', credentials);

    // Clear cache after updating credentials
    this.jiraConfigCache = null;
    this.jiraConfigPromise = null;

    return response.data;
  }

  async getRealtimeWorklogs() {
    try {
      const response = await this.api.get('/realtime-worklogs');
      return response.data;
    } catch (error) {
      console.error('Error fetching real-time worklogs:', error);
      throw error;
    }
  }

  async getUserDayIssues(accountId: string, date: string, logType?: string) {
    const params: any = { accountId, date };
    if (logType) params.log_type = logType;
    const response = await this.api.get('/reports/user-day-issues', { params });
    return response.data;
  }

  async getUserDayLogType(accountId: string, date: string) {
    const response = await this.api.get('/reports/user-day-log-type', { params: { accountId, date } });
    return response.data;
  }

  async getUserMonthLogType(accountId: string, year: number, month: number) {
    const response = await this.api.get('/reports/user-month-log-type', { params: { accountId, year, month } });
    return response.data;
  }

  // Settings API methods
  async syncUsers() {
    const response = await this.api.get('/sync-users');
    return response.data;
  }

  async syncIssues() {
    const response = await this.api.get('/sync-issues');
    return response.data;
  }

  async syncIssuesTruncate() {
    const response = await this.api.get('/sync-issues', { params: { truncate: 'true', projects: 'all', issueTypes: 'all' } });
    return response.data;
  }

  async syncIssuesFull() {
    const response = await this.api.get('/sync-issues', { params: { fetchAll: 'true', projects: 'all', issueTypes: 'all', source: 'settings' } });
    return response.data;
  }

  async getWorklogs(force: boolean = false) {
    const response = await this.api.get('/worklog', {
      params: force ? { force: 'true' } : {}
    });
    return response.data;
  }

  async refreshWorklogs() {
    const response = await this.api.get('/worklog', { params: { source: 'refresh' } });
    return response.data;
  }

  async getWorklogsCount() {
    const response = await this.api.get('/db/worklogs/count');
    return response.data;
  }

  async syncWorklogsTruncate() {
    const response = await this.api.get('/worklog', { params: { truncate: 'true' } });
    return response.data;
  }

  async syncWorklogsFull() {
    const response = await this.api.get('/worklog', { params: { fetchAll: 'true' } });
    return response.data;
  }

  async syncStatusLog() {
    const response = await this.api.post('/status-logs/sync');
    return response.data;
  }

  // Recent sync jobs for the Update data "Sync jobs" table.
  async getSyncJobs() {
    const response = await this.api.get('/sync-jobs');
    return response.data;
  }

  // HTTP method wrappers for report service
  async get(url: string, config?: any) {
    return this.api.get(url, config);
  }

  async post(url: string, data?: any, config?: any) {
    return this.api.post(url, data, config);
  }

  async put(url: string, data?: any, config?: any) {
    return this.api.put(url, data, config);
  }

  async delete(url: string, config?: any) {
    return this.api.delete(url, config);
  }

  async patch(url: string, data?: any, config?: any) {
    return this.api.patch(url, data, config);
  }

  // Epic report (Reports page). `period` is a month (2026-10), a quarter (2026-Q4) or a year (2026).
  // Lists, counts and the download all read the same roster on the server, so they agree.
  async getEpicRoster(period: string, tab: string) {
    const response = await this.api.get('/reports/monthly-epics', { params: { period, tab } });
    return response.data;
  }

  async getEpicTabCounts(period: string) {
    const response = await this.api.get('/reports/monthly-epics/tab-counts', { params: { period } });
    return response.data;
  }

  // A file from the server. A failed download comes back as a blob too, so the server's message is
  // read out of it.
  private async fetchFile(url: string, params: Record<string, unknown>): Promise<Blob> {
    try {
      const response = await this.api.get(url, { params, responseType: 'blob' });
      return response.data as Blob;
    } catch (e: any) {
      const body = e?.response?.data;
      let message: string | null = null;
      if (typeof Blob !== 'undefined' && body instanceof Blob) {
        try {
          message = JSON.parse(await body.text())?.error ?? null;
        } catch {
          message = null;
        }
      }
      throw new Error(message || e?.message || 'The download could not be created');
    }
  }

  // The whole epic report as one .xlsx.
  async downloadEpicReport(period: string): Promise<Blob> {
    return this.fetchFile('/reports/monthly-epics/export', { period });
  }

  // Step 3: plain-language summary of one epic, as of the end of the period the report shows.
  // The Reports page sends its period; the Planning page sends its own from/to.
  async getEpicSummary(issueKey: string, range: { period: string } | { from: string; to: string }) {
    const response = await this.api.get(`/reports/epics/${encodeURIComponent(issueKey)}/summary`, { params: range });
    return response.data;
  }

  // User Worklogs: the people × days grid for a month, and the same as one .xlsx (optionally one group).
  async getUserMonthly(year: number, month: number) {
    const response = await this.api.get('/reports/summary/user-monthly', { params: { year, month } });
    return response.data;
  }

  async downloadUserMonthly(year: number, month: number, group?: string): Promise<Blob> {
    return this.fetchFile('/reports/summary/user-monthly/export', { year, month, group: group || undefined });
  }

  // Worklog hours by tag (log_type) × group for a month ("2026-10") — the "Tags" time-distribution view.
  async getWorklogTagDistribution(period: string) {
    return this.api.get('/reports/worklog-tag-distribution', { params: { period } });
  }

  async getWorklogDetails(issueId: string, accountId?: string, date?: string) {
    let url = `/reports/worklog-details?issueId=${issueId}`;
    if (accountId) {
      url += `&accountId=${accountId}`;
    }
    if (date) {
      url += `&date=${date}`;
    }
    return this.api.get(url);
  }

  // User management APIs
  async createUser(userData: {
    username: string;
    email: string;
    password: string;
    full_name: string;
    role?: string;
  }) {
    return await this.api.post('/auth/register', userData);
  }

  async getAllUsers() {
    return await this.api.get('/auth/users');
  }

  async updateUser(userId: number, userData: {
    username?: string;
    email?: string;
    password?: string;
    full_name?: string;
    role?: string;
  }) {
    return await this.api.put(`/auth/users/${userId}`, userData);
  }

  async updateUserRole(userId: number, role: string) {
    return await this.api.patch(`/auth/users/${userId}/roles`, { role });
  }

  async deleteUser(userId: number) {
    return await this.api.delete(`/auth/users/${userId}`);
  }

  // A User's assigned curated teams (for scoping)
  async getUserTeams(userId: number) {
    return await this.api.get(`/auth/users/${userId}/teams`);
  }

  async setUserTeams(userId: number, teamIds: number[]) {
    return await this.api.put(`/auth/users/${userId}/teams`, { team_ids: teamIds });
  }

  // Login <-> Jira person link (Settings -> Users). accountId null removes the link.
  async setJiraLink(userId: number, accountId: string | null) {
    return await this.api.put(`/auth/users/${userId}/jira-link`, { account_id: accountId });
  }

  // Runs the automatic email rule for every login that has no link yet.
  async matchLoginsByEmail() {
    return await this.api.post('/auth/jira-links/auto');
  }

  // Jira Users (for team member selection)
  async getJiraUsers() {
    return await this.api.get('/db/users');
  }

  // Distinct Jira user group names (for quick-filter chips)
  async getDistinctUserGroups() {
    return await this.api.get('/db/distinct-user-groups');
  }


  // Team Management APIs
  async getAllTeams() {
    return await this.api.get('/auth/teams');
  }

  async createTeam(teamData: { name: string; assigned_user_ids?: number[] }) {
    return await this.api.post('/auth/teams', teamData);
  }

  async updateTeam(teamId: number, teamData: { name?: string; assigned_user_ids?: number[] }) {
    return await this.api.put(`/auth/teams/${teamId}`, teamData);
  }

  async deleteTeam(teamId: number) {
    return await this.api.delete(`/auth/teams/${teamId}`);
  }

  async getTeamMembers(teamId: number) {
    return await this.api.get(`/auth/teams/${teamId}/members`);
  }

  async addTeamMember(teamId: number, jiraUserAccountId: string) {
    return await this.api.post(`/auth/teams/${teamId}/members`, { jira_user_account_id: jiraUserAccountId });
  }

  async removeTeamMember(teamId: number, accountId: string) {
    return await this.api.delete(`/auth/teams/${teamId}/members/${accountId}`);
  }

  async getAssignableUsers() {
    return await this.api.get('/auth/assignable-users');
  }

  // Email Notifications
  async sendNotificationEmail(params: {
    toEmail: string;
    toName: string;
    subject: string;
    message: string;
  }) {
    return await this.api.post('/notifications/send-email', params);
  }

  async getSmtpStatus() {
    return await this.api.get('/notifications/smtp-status');
  }

  // SMTP Configuration
  async getSmtpConfig() {
    return await this.api.get('/smtp-config');
  }

  async updateSmtpConfig(config: {
    host: string;
    port: number;
    secure: boolean;
    username: string;
    password: string;
    fromEmail: string;
    fromName: string;
  }) {
    return await this.api.put('/smtp-config', config);
  }

  async testSmtpConnection(config: {
    host: string;
    port: number;
    secure: boolean;
    username: string;
    password: string;
  }) {
    return await this.api.post('/smtp-config/test', config);
  }

  // Task 4: Jira custom-field mapping (admin only).
  async getCustomFieldMap() {
    const response = await this.api.get('/admin/custom-fields');
    return response.data;
  }

  async updateCustomFieldMap(logicalName: string, body: { project_key?: string; jira_field_id: string | null; notes?: string | null }) {
    const response = await this.api.put(`/admin/custom-fields/${encodeURIComponent(logicalName)}`, body);
    return response.data;
  }

  async getJiraCustomFields() {
    const response = await this.api.get('/admin/jira/fields');
    return response.data;
  }

  // Task 5: team ↔ project/board attachments (admin only).
  async getTeamProjects(teamId: number) {
    const response = await this.api.get(`/admin/teams/${teamId}/projects`);
    return response.data;
  }

  async attachTeamProject(teamId: number, body: { project_key: string; board_id?: number | null }) {
    const response = await this.api.post(`/admin/teams/${teamId}/projects`, body);
    return response.data;
  }

  async detachTeamProject(teamId: number, projectKey: string, boardId?: number | null) {
    const params = boardId != null ? { board_id: boardId } : undefined;
    const response = await this.api.delete(`/admin/teams/${teamId}/projects/${encodeURIComponent(projectKey)}`, { params });
    return response.data;
  }

  // Step 4: Flow. Checks (summary, handoffs, rework) follow Hygiene visibility and give leaders 403;
  // waiting time and stuck tickets are open to everyone; roles and statuses are admin only.
  async getFlowSummary(params: { period: string; mine?: boolean }) {
    const response = await this.api.get('/flow/summary', { params: { period: params.period, mine: params.mine ? 'true' : undefined } });
    return response.data;
  }

  async getFlowHandoffs(params: { period: string; project_key?: string; mine?: boolean; limit?: number; offset?: number }) {
    const response = await this.api.get('/flow/handoffs', { params: { ...params, mine: params.mine ? 'true' : undefined } });
    return response.data;
  }

  async getFlowRework(params: { period: string; project_key?: string; mine?: boolean }) {
    const response = await this.api.get('/flow/rework', { params: { ...params, mine: params.mine ? 'true' : undefined } });
    return response.data;
  }

  async getFlowWaiting(params: { period: string; project_key?: string }) {
    const response = await this.api.get('/flow/waiting', { params });
    return response.data;
  }

  async getFlowStuck(params: { project_key?: string; status?: string; max_age_days?: number; limit?: number; offset?: number }) {
    const response = await this.api.get('/flow/stuck', { params });
    return response.data;
  }

  async getFlowRoles() {
    const response = await this.api.get('/flow/roles');
    return response.data;
  }

  async setFlowRoles(accountId: string, roles: string[]) {
    const response = await this.api.put(`/flow/roles/${encodeURIComponent(accountId)}`, { roles });
    return response.data;
  }

  async applyFlowSuggestions() {
    const response = await this.api.post('/flow/roles/apply-suggestions');
    return response.data;
  }

  async getFlowStatuses() {
    const response = await this.api.get('/flow/statuses');
    return response.data;
  }

  async updateFlowStatus(status: string, body: { owner_roles?: string[]; stuck_after_days?: number | null }) {
    const response = await this.api.put(`/flow/statuses/${encodeURIComponent(status)}`, body);
    return response.data;
  }

  async addFlowAlias(alias: string, status: string) {
    const response = await this.api.post('/flow/aliases', { alias, status });
    return response.data;
  }

  async removeFlowAlias(alias: string) {
    const response = await this.api.delete(`/flow/aliases/${encodeURIComponent(alias)}`);
    return response.data;
  }

  // Effort: how time is logged, and where it is missing.
  async getEffortSummary(params: { period: string; mine?: boolean }) {
    const response = await this.api.get('/effort/summary', { params: { period: params.period, mine: params.mine ? 'true' : undefined } });
    return response.data;
  }

  async getEffortFixes(params: { period: string; project_key?: string; mine?: boolean; limit?: number; offset?: number }) {
    const response = await this.api.get('/effort/fixes', { params: { ...params, mine: params.mine ? 'true' : undefined } });
    return response.data;
  }

  async getEffortGaps(params: { project_key?: string } = {}) {
    const response = await this.api.get('/effort/gaps', { params });
    return response.data;
  }

  // The Plan: the quarter planning sheet, made in the portal.
  async getPlan(quarter: string | null, projectKey?: string | null) {
    const response = await this.api.get('/plan', { params: { ...(quarter ? { quarter } : {}), ...(projectKey ? { project_key: projectKey } : {}) } });
    return response.data;
  }

  async getPlanRoadmap(quarter: string | null) {
    const response = await this.api.get('/plan/roadmap', { params: quarter ? { quarter } : {} });
    return response.data;
  }

  async getPlanFunctions(quarter: string | null) {
    const response = await this.api.get('/plan/functions', { params: quarter ? { quarter } : {} });
    return response.data;
  }

  async getPlanIntake(period: string | null) {
    const response = await this.api.get('/plan/intake', { params: period ? { period } : {} });
    return response.data;
  }

  async downloadPlan(quarter: string): Promise<Blob> {
    return this.fetchFile('/plan/export', { quarter });
  }

  async updatePlanNote(issueKey: string, body: Record<string, unknown>) {
    const response = await this.api.put(`/plan/notes/${encodeURIComponent(issueKey)}`, body);
    return response.data;
  }

  async addPlanIdea(body: Record<string, unknown>) {
    const response = await this.api.post('/plan/ideas', body);
    return response.data;
  }

  async updatePlanIdea(id: number, body: Record<string, unknown>) {
    const response = await this.api.put(`/plan/ideas/${id}`, body);
    return response.data;
  }

  async dropPlanIdea(id: number) {
    const response = await this.api.delete(`/plan/ideas/${id}`);
    return response.data;
  }

  async attachPlanIdea(id: number, issueKey: string) {
    const response = await this.api.post(`/plan/ideas/${id}/attach`, { issue_key: issueKey });
    return response.data;
  }

  async importPlanSheet(csv: string, filename: string, quarter: string, dryRun: boolean) {
    const response = await this.api.post('/plan/import', { csv, filename, quarter, dry_run: dryRun });
    return response.data;
  }

  async getPlanImports() {
    const response = await this.api.get('/plan/imports');
    return response.data;
  }

  // The Tech report: the mid-month and monthly report, made from Jira.
  async getTechReport(period: string, half: string) {
    const response = await this.api.get('/reports/tech', { params: { period, half } });
    return response.data;
  }

  async downloadTechReport(period: string, half: string, format: 'md' | 'docx'): Promise<Blob> {
    return this.fetchFile('/reports/tech/export', { period, half, format });
  }

  async keepTechReport(period: string, half: string, format: 'md' | 'docx') {
    const response = await this.api.post('/reports/tech/keep', { period, half, format });
    return response.data;
  }

  async getTechReportFiles(period: string) {
    const response = await this.api.get('/reports/tech/files', { params: { period } });
    return response.data;
  }

  async downloadTechReportFile(id: number): Promise<Blob> {
    return this.fetchFile(`/reports/tech/files/${id}`, {});
  }

  async deleteTechReportFile(id: number) {
    const response = await this.api.delete(`/reports/tech/files/${id}`);
    return response.data;
  }

  async saveTechReportRoadblocks(period: string, half: string, roadblocks: string) {
    const response = await this.api.put('/reports/tech/notes', { period, half, roadblocks });
    return response.data;
  }

  // Where "Before <month>" starts: the day the Jira conventions began (a setting in Standards).
  async getBoundaries() {
    const response = await this.api.get('/config/boundaries');
    return response.data;
  }

  // When the issue sync last finished well, for the line beside Tech and the Report.
  async getFreshness() {
    const response = await this.api.get('/config/freshness');
    return response.data;
  }

  // Settings → Update data: the incremental jobs (issues, status moves, worklogs), in order, in the background.
  async updateFromJiraNow() {
    const response = await this.api.post('/admin/update-now');
    return response.data;
  }

  // Planning: how far each quarter's goal epics have come, and how each sprint went.
  // Standards editor (Settings -> Standards).
  async getStandards() {
    const response = await this.api.get('/standards');
    return response.data;
  }

  async setStandardSetting(area: string, key: string, value: number | string) {
    const response = await this.api.put(`/standards/settings/${area}/${key}`, { value });
    return response.data;
  }

  async resetStandardSetting(area: string, key: string) {
    const response = await this.api.delete(`/standards/settings/${area}/${key}`);
    return response.data;
  }

  async addStandardTag(tag: string, roleGroup: string, description?: string) {
    const response = await this.api.post('/standards/tags', { tag, role_group: roleGroup, description });
    return response.data;
  }

  async removeStandardTag(tag: string) {
    const response = await this.api.delete(`/standards/tags/${encodeURIComponent(tag)}`);
    return response.data;
  }

  async addStandardAlias(alias: string, tag: string) {
    const response = await this.api.post('/standards/tag-aliases', { alias, tag });
    return response.data;
  }

  async removeStandardAlias(alias: string) {
    const response = await this.api.delete(`/standards/tag-aliases/${encodeURIComponent(alias)}`);
    return response.data;
  }

  async getStandardsChangelog(limit = 50) {
    const response = await this.api.get('/standards/changelog', { params: { limit } });
    return response.data;
  }

  // Compliance: the admin overview, and a person's own audit.
  async getComplianceOverview(period: string) {
    const response = await this.api.get('/compliance/overview', { params: { period } });
    return response.data;
  }

  async getMyAudit(kind: 'summary' | 'naming' | 'flow' | 'timelog' | 'planning' | 'history', period: string) {
    const response = await this.api.get(`/me/audit/${kind}`, { params: { period } });
    return response.data;
  }

  /** Tech: what is happening across the five functions, for a leader's or admin's Home. */
  async getTech(period?: string) {
    const response = await this.api.get('/tech', { params: period ? { period } : {} });
    return response.data;
  }

  /** Home: one short answer shaped by the signed-in role. */
  async getMyHome(period?: string) {
    const response = await this.api.get('/me/home', { params: period ? { period } : {} });
    return response.data;
  }

  /** The people in the reporting lines the signed-in login manages or is assigned to (a leader or an admin may name a team). */
  async getMyPeople(period?: string, teamId?: number) {
    const response = await this.api.get('/me/people', { params: { ...(period ? { period } : {}), ...(teamId ? { team_id: teamId } : {}) } });
    return response.data;
  }

  /** Search from the Home box: tickets, people and projects the signed-in person may open. */
  async searchPortal(q: string, signal?: AbortSignal) {
    const response = await this.api.get('/search', { params: { q }, signal });
    return response.data;
  }

  /** What changed in the signed-in person's projects over the last few days (the person is the login's own). */
  async getMyUpdates(days?: number) {
    const response = await this.api.get('/me/updates', { params: days ? { days } : {} });
    return response.data;
  }

  /** Every project with its owner, the people involved and its teams (admin, for Settings → Projects). */
  async getProjectRoles() {
    const response = await this.api.get('/projects/roles');
    return response.data;
  }

  /** Replaces a project's owner (an account id, or null) and the people involved (admin). */
  async setProjectRoles(projectKey: string, owner: string | null, involved: string[]) {
    const response = await this.api.put(`/projects/${encodeURIComponent(projectKey)}/roles`, { owner, involved });
    return response.data;
  }

  /** What the "Give kudos" form can choose from: teams, people and projects (leaders and admins). */
  async getKudosOptions() {
    const response = await this.api.get('/kudos/options');
    return response.data;
  }

  /** The kudos the signed-in leader or admin gave, newest first. */
  async getKudosGiven(limit?: number) {
    const response = await this.api.get('/kudos', { params: limit ? { limit } : {} });
    return response.data;
  }

  /** Every kudos, newest first, a page at a time (admin, for Settings → Kudos). `after` is the cursor of the last row seen. */
  async getKudosAll(params: { limit?: number; after?: string; q?: string } = {}) {
    const response = await this.api.get('/kudos/all', { params });
    return response.data;
  }

  async giveKudos(body: { to_kind: 'team' | 'person'; to_team_id?: number; to_account_id?: string; project_key?: string | null; emoji: string; note?: string | null }) {
    const response = await this.api.post('/kudos', body);
    return response.data;
  }

  async deleteKudos(id: number) {
    const response = await this.api.delete(`/kudos/${id}`);
    return response.data;
  }

  async getPlanningQuarters() {
    const response = await this.api.get('/planning/quarters');
    return response.data;
  }

  async getPlanningQuarter(quarter: string, params: { project_key?: string } = {}) {
    const response = await this.api.get(`/planning/quarters/${encodeURIComponent(quarter)}`, { params });
    return response.data;
  }

  async getPlanningSprints(params: { project_key?: string; quarter?: string } = {}) {
    const response = await this.api.get('/planning/sprints', { params });
    return response.data;
  }

  async getPlanningSprint(id: number, params: { refresh?: boolean } = {}) {
    const response = await this.api.get(`/planning/sprints/${id}`, { params: { refresh: params.refresh ? 'true' : undefined } });
    return response.data;
  }

  // Step 2: naming compliance engine (coach mode). Leaders get 403 by design.
  async getNamingSummary(params: { period: string; project_key?: string; mine?: boolean }) {
    const response = await this.api.get('/naming/summary', { params: { ...params, mine: params.mine ? 'true' : undefined } });
    return response.data;
  }

  async getNamingResults(params: {
    period?: string; project_key?: string; issue_type?: string; assignee?: string;
    verdict?: 'attention' | 'clear' | 'all'; open_only?: boolean; mine?: boolean; limit?: number; offset?: number;
  }) {
    const response = await this.api.get('/naming/results', {
      params: { ...params, open_only: params.open_only === false ? 'false' : undefined, mine: params.mine ? 'true' : undefined },
    });
    return response.data;
  }

  async getNamingMe() {
    const response = await this.api.get('/naming/me');
    return response.data;
  }

  async getNamingRules() {
    const response = await this.api.get('/naming/rules');
    return response.data;
  }

  async updateNamingRule(key: string, body: { active?: boolean; message?: string; suggestion?: string | null; pattern?: string | null; issue_types?: string[] | null }) {
    const response = await this.api.put(`/naming/rules/${encodeURIComponent(key)}`, body);
    return response.data;
  }

  async getNamingVerbs() {
    const response = await this.api.get('/naming/verbs');
    return response.data;
  }

  async addNamingVerb(verb: string) {
    const response = await this.api.post('/naming/verbs', { verb });
    return response.data;
  }

  async removeNamingVerb(verb: string) {
    const response = await this.api.delete(`/naming/verbs/${encodeURIComponent(verb)}`);
    return response.data;
  }

  async runNamingEvaluate() {
    const response = await this.api.post('/naming/evaluate');
    return response.data;
  }

  // Task 6: self-service password change (also clears must_change_password).
  async changePassword(currentPassword: string, newPassword: string) {
    const response = await this.api.post('/auth/change-password', { currentPassword, newPassword });
    return response.data;
  }
}

export const apiService = new ApiService();
export default apiService;
